import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { CirclePlus, RefreshCw } from "lucide-react";
import { useState } from "react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { TextInput } from "@/shared/components/form/text-input";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useSamplesLaboratoryPutMutation } from "@/services/backend/samples/api/lab-api";
import {
  LabApiKey,
  Laboratory,
} from "@/services/backend/samples/types/laboratory";
import { newAddress } from "@/services/backend/addresses/types/address";

function newLaboratory(): Laboratory {
  return {
    id: v4(),
    name: "",
    address: newAddress(),
    api: LabApiKey.Zero,
    analysesAirMicrobialSamples: false,
    analysesDustDensitySamples: false,
    analysesSurfaceMicrobialSamples: false,
    analysesWaterSamples: false,
  };
}

export function CreateLabDialog({
  onSuccess,
}: {
  onSuccess?: (lab: Laboratory) => void;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<Laboratory>(newLaboratory());
  const [create, { isLoading, error, isSuccess, reset }] =
    useSamplesLaboratoryPutMutation();

  const doCreate = () => {
    if (isLoading) {
      return;
    }
    create(request);
  };

  if (isSuccess) {
    setOpen(false);
    reset();
    onSuccess?.(request);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) {
          setRequest(newLaboratory());
        }
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <CirclePlus />
          <span>{t("Labor")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Labor erstellen")}</DialogTitle>
          <DialogDescription>
            {t(
              "Es muss ein Name festgelegt werden. Adresse, API-Anbindung und Analysearten können anschließend in der Detailansicht konfiguriert werden.",
            )}
          </DialogDescription>
        </DialogHeader>
        <TextInput
          label={t("Name des Labors")}
          field="name"
          text={request.name}
          onTextChange={(name) => setRequest({ ...request, name })}
          errors={inputValidationErrorMessages(error, "name")}
        />
        <RTKQueryErrorAlert error={error} />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">{t("Abbrechen")}</Button>
          </DialogClose>
          <Button onClick={doCreate} disabled={isLoading}>
            {isLoading && <RefreshCw className="mr-2 animate-spin" />}
            {t("Erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
